import type { CSSProperties } from "react";
import { Link, useNavigate } from "react-router-dom";
import { clerkEnabled } from "@/services/clerk";
import { EVENTS_URL, LISTSERV_URL } from "@/components/AppSidebar";
import { sayListservIsForMembers } from "@/lib/listserv";
import { useMe } from "@/features/members/hooks/useMe";
import { useApplyCta } from "@/features/members/hooks/useApplyCta";
import { usePublicConfig } from "@/features/applications/hooks/useApplications";
import { Button } from "@/components/ui/button";

/** Entrance stagger index — see --stagger in styles/globals.css. */
const stagger = (i: number) => ({ "--stagger": i }) as CSSProperties;

// Each heading is the way to the thing it names. Todd, after the 1 September
// demo: "from public landing page, link to directory where mentioned." The
// third is the listserv, called Discussion as it is in the sidebar (Tracy
// Bartley, 11 September, found "Community" here and "Discussion" there, and
// only one of them a link). It opens the Google Group for a member and, for
// anyone else, says what the sidebar says rather than sending a stranger to a
// Google page that turns them away.
const features = [
  {
    title: "Member directory",
    href: "/directory",
    description: "Browse and search a private directory of members and the organizations they steward: estates, foundations and studios.",
  },
  {
    title: "Resource library",
    href: "/resources",
    description: "Guides, templates, and vetted references for legal, archival, and financial questions.",
  },
  {
    title: "Discussion",
    listserv: true,
    description: "A members-only listserv and network of peers who have navigated the same questions.",
  },
];

/**
 * The front page, at `/`, inside the app's own chrome.
 *
 * It used to be a separate page with its own header and footer, which meant a
 * visitor arriving from Soft Network met a brochure with one button on it and
 * had to sign up before seeing anything. Now the sidebar is here from the
 * first screen: the directory, the resources and the help centre are all one
 * click away, in preview, whether or not there is a session. What is left for
 * this page is what AFELL is, and the way to join it.
 */
export default function WelcomePage() {
  const navigate = useNavigate();
  const { data: me } = useMe(clerkEnabled);
  return (
    <div className="max-w-5xl">
      <section className="mx-auto max-w-3xl px-6 py-16 text-center">
        {/* No eyebrow: the sidebar's lockup already spells the acronym out,
            and repeating it here reads as a stutter. */}
        {/* softnetwork.art's scale: nudista at 72px, regular weight, -0.02em.
            Big and plain is the house style; a smaller, greyer hero read as
            a template. */}
        <h1
          className="animate-item-enter text-5xl tracking-[-0.02em] sm:text-6xl md:text-7xl"
          style={stagger(0)}
        >
          {/* Chelsea, 11 September: "It's important that AFELL is understood
              as a network of stewards AND artists - not just stewards." Her
              words, both sentences. */}
          A private network for artists and stewards of artists&apos; estates
          and legacies
        </h1>
        <p
          className="animate-item-enter mx-auto mt-8 max-w-2xl text-lg leading-relaxed sm:text-xl"
          style={stagger(1)}
        >
          AFELL connects artists and the people who care for artists&apos; work
          after a lifetime, with a member directory, shared resources,
          discussion group, and community-led events.
        </p>
        <div
          className="animate-item-enter mt-8 flex justify-center gap-3"
          style={stagger(2)}
        >
          <HeroCta />
        </div>
      </section>

      {/* Prose, not cards: three headed paragraphs, no borders, no icons —
          the way softnetwork.art sets its own sections. The bordered
          icon-card row is the most recognisable template pattern there is. */}
      <section className="mx-auto grid max-w-5xl gap-10 px-6 pb-20 sm:grid-cols-3 sm:gap-8">
        {features.map((feature, i) => (
          <div
            key={feature.title}
            className="animate-item-enter"
            style={stagger(4 + i)}
          >
            <h2 className="text-2xl">
              {feature.href ? (
                <Link to={feature.href} className="underline-offset-4 hover:underline">
                  {feature.title}
                </Link>
              ) : feature.listserv && me?.isMember ? (
                <a
                  href={LISTSERV_URL}
                  target="_blank"
                  rel="noreferrer"
                  className="underline-offset-4 hover:underline"
                >
                  {feature.title}
                </a>
              ) : feature.listserv && me ? (
                <button
                  type="button"
                  onClick={() => sayListservIsForMembers(navigate)}
                  className="cursor-pointer underline-offset-4 hover:underline"
                >
                  {feature.title}
                </button>
              ) : (
                // Not known yet whether they are a member: a name, and nothing
                // to press that might send them the wrong way.
                feature.title
              )}
            </h2>
            <p className="mt-3 text-base leading-relaxed">{feature.description}</p>
          </div>
        ))}
      </section>

      <SiteFooter />
    </div>
  );
}

/**
 * The three things somebody reads a front page for that are not the pitch:
 * what they would be agreeing to, and how to reach a person.
 *
 * The policies are the same two an applicant accepts on the last step of the
 * form, and their URLs come from those very fields — so an administrator
 * moving them in `/basement/` moves them here too. The address is for
 * somebody with no account: the help page's contact form does the same job
 * for a member, and needs a session this reader does not have.
 */
function SiteFooter() {
  const { data: config } = usePublicConfig();
  const terms = config?.policies?.terms;
  const privacy = config?.policies?.privacy;
  const contact = config?.contactEmail;
  return (
    <footer className="text-muted-foreground mx-auto flex max-w-5xl flex-wrap items-center justify-center gap-x-6 gap-y-2 border-t px-6 py-8 text-sm">
      {/* Attribution, which is what a footer is for. It sat under the hero's
          button, where it competed with the one thing the page asks somebody
          to do and interrupted the run from the pitch to what membership is.
          Soft Network's own site is where a reader following it wants to go. */}
      <span>
        A program of{" "}
        <a
          href="https://www.softnetwork.art"
          target="_blank"
          rel="noreferrer"
          className="underline-offset-4 hover:underline"
        >
          Soft Network
        </a>
      </span>
      {/* Where the AFELL Zoom sessions are listed and registered for, on Soft
          Network's own site. Also in the sidebar, which this page carries, but
          a reader who scrolled the pitch to the bottom is exactly somebody
          looking for what happens next. */}
      <a
        href={EVENTS_URL}
        target="_blank"
        rel="noreferrer"
        className="underline-offset-4 hover:underline"
      >
        Events
      </a>
      {terms && (
        <a href={terms} target="_blank" rel="noreferrer" className="underline-offset-4 hover:underline">
          Terms of Use
        </a>
      )}
      {privacy && (
        <a href={privacy} target="_blank" rel="noreferrer" className="underline-offset-4 hover:underline">
          Privacy Policy
        </a>
      )}
      {contact && (
        <a href={`mailto:${contact}`} className="underline-offset-4 hover:underline">
          {contact}
        </a>
      )}
    </footer>
  );
}

/**
 * One button, and which one depends on what the reader already has.
 *
 * A member has nothing to apply for, so they get the directory. Everybody else
 * — signed out or signed in on the preview — gets the application, which is
 * the only thing this page is asking of them. Signed out, `/apply` sends them
 * through sign-in first and returns them to it.
 */
function HeroCta() {
  const { data: me } = useMe(clerkEnabled);
  const label = useApplyCta();
  if (me?.isMember) {
    return (
      <Button size="lg" asChild>
        <Link to="/directory">Open directory</Link>
      </Button>
    );
  }
  return (
    <Button size="lg" variant="brand" asChild>
      <Link to="/apply">{label}</Link>
    </Button>
  );
}
